import { useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';
import type { ApiEndpoints, ProjectUndeliveredReason } from '@avisoma/shared';
import { PageHeader } from '../../components/PageHeader';
import { TableSkeleton } from '../../components/Skeleton';
import { SortableTable, type SortableTableColumn } from '../../components/SortableTable';
import Pagination from '../../components/Pagination';
import { Link } from '../../components/Link';
import { ID } from '../../components/ID';
import CopyButton from '../../components/CopyButton';
import UndeliveredReasonFilter from '../../components/filters/UndeliveredReasonFilter';
import { useFilterState } from '../../components/filters/useFilterState';
import {
  UndeliveredFamilyChips,
  UNDELIVERED_REASON_META,
  listUndeliveredFamilies,
} from '../../components/undeliveredFamilies';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import { formatDateTime, formatRelativeTime } from '../../lib/dateFormat';
import backend from '../../services/backend';

// Page-based pagination, matching the sibling Projects list. The endpoint caps
// `limit` at 500, so the bumped-limit infinite-scroll strategy would silently
// clamp — while still reporting hasMore — once the window outgrew the cap.
const PAGE_SIZE = 50;
const AUTO_RELOAD_MS = 30_000;
const FILTER_STORAGE_KEY = 'undelivered-events-filters';

interface UndeliveredEventsFilters {
  /** A `ProjectUndeliveredReason`, or '' for no filter. */
  reason: string;
}

const DEFAULT_FILTERS: UndeliveredEventsFilters = { reason: '' };

type UndeliveredRow =
  ApiEndpoints<string>['/api/projects/undelivered-events']['GET']['response']['items'][number];

function isUndeliveredReason(value: string): value is ProjectUndeliveredReason {
  return value in UNDELIVERED_REASON_META;
}

const columns: SortableTableColumn<UndeliveredRow>[] = [
  {
    label: 'Project',
    accessor: 'id',
    render: (row) => (
      <span className="flex items-center gap-2">
        <Link
          to={`/projects/${row.id}`}
          className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
        >
          <ID id={row.id} />
        </Link>
        <CopyButton text={row.id} />
      </span>
    ),
  },
  {
    label: 'Owed families',
    accessor: 'undeliveredFamilies',
    sortable: true,
    sortValue: (row) => listUndeliveredFamilies(row.undeliveredFamilies).length,
    render: (row) => <UndeliveredFamilyChips families={row.undeliveredFamilies} />,
  },
  {
    label: 'Last updated',
    accessor: 'updatedAt',
    sortable: true,
    render: (row) => (
      <span className="text-gray-500 dark:text-zinc-400" title={formatDateTime(row.updatedAt)}>
        {formatRelativeTime(row.updatedAt)}
      </span>
    ),
  },
];

export default function UndeliveredEvents() {
  useDocumentTitle('Undelivered to Oil');
  const [page, setPage] = useState(1);

  // sessionStorage-backed so a narrowed sweep survives a reload or a trip to a
  // project and back — otherwise the admin lands on the unfiltered list and the
  // permanent empty-snapshot population buries the actionable rows again.
  // No debounced side-effect needed: the query derives from `filters` directly,
  // and useFilterState seeds them synchronously from storage (as Audit.tsx does).
  const { filters, setFilters } = useFilterState<UndeliveredEventsFilters>({
    storageKey: FILTER_STORAGE_KEY,
    defaults: DEFAULT_FILTERS,
    onFilterChange: () => {},
  });
  const { reason } = filters;

  const undeliveredQuery = useQuery({
    queryKey: ['projects-undelivered-events', page, reason],
    queryFn: () =>
      backend
        .listUndeliveredEvents({
          page,
          limit: PAGE_SIZE,
          reason: isUndeliveredReason(reason) ? reason : undefined,
        })
        .then((res) => res.data),
    placeholderData: keepPreviousData,
    refetchInterval: AUTO_RELOAD_MS,
  });

  // A filter change re-selects the population, so page 4 of the previous result
  // set is meaningless — snap back to the first page.
  const handleReasonChange = (value: string) => {
    setFilters({ reason: value });
    setPage(1);
  };

  const items = undeliveredQuery.data?.items ?? [];
  const hasNextPage = undeliveredQuery.data?.hasMore ?? false;

  return (
    <div className="p-4 md:p-6 space-y-6">
      <PageHeader
        title="Undelivered to Oil"
        description="Projects still owing Oil at least one rollup family"
        icon={<ExclamationTriangleIcon className="size-6" />}
      />

      <div className="flex flex-wrap items-end gap-4">
        <div className="min-w-48">
          <UndeliveredReasonFilter reason={reason} onReasonChange={handleReasonChange} />
        </div>
      </div>

      {undeliveredQuery.isLoading && <TableSkeleton />}

      {undeliveredQuery.error && (
        <div className="text-red-600 dark:text-red-400 text-center text-sm pt-12">
          {(undeliveredQuery.error as Error).message}
        </div>
      )}

      {!undeliveredQuery.isLoading && !undeliveredQuery.error && (
        <div className="flex flex-col gap-2">
          <div
            className={clsx(
              'bg-white dark:bg-zinc-800 shadow dark:shadow-zinc-950/30 rounded-lg dark:border dark:border-zinc-700 overflow-hidden transition-opacity',
              undeliveredQuery.isPlaceholderData && 'opacity-50 pointer-events-none',
            )}
          >
            {items.length > 0 ? (
              <SortableTable
                columns={columns}
                data={items}
                rowKey={(row) => row.id}
                storageId="projects-undelivered-events"
                defaultSortBy="updatedAt"
                defaultSortDir="desc"
                limitHeight={false}
              />
            ) : (
              <p className="p-8 text-center text-sm text-gray-500 dark:text-zinc-400">
                {page > 1
                  ? 'No projects on this page.'
                  : reason
                    ? 'No projects owe Oil anything for this reason.'
                    : 'Every project is fully delivered to Oil.'}
              </p>
            )}
          </div>

          {(page > 1 || hasNextPage) && (
            <Pagination page={page} handlePageChange={setPage} hasNextPage={hasNextPage} />
          )}
        </div>
      )}
    </div>
  );
}
